import React, { useMemo } from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import {
  GetProjectsDocument,
  GetProjectsQueryVariables,
  useCreateProjectMutation,
} from '@sketch/gql-types'
import type {
  FormValues,
  FormActions,
  AddNewProjectModalProps,
  CreateProjectWorkspaceAccessLevel,
  PendingGuestMember,
} from './types'
import { FormikRender, MAX_LENGTH } from './AddNewProjectModalForm'
import { FolderTerm, useFolderTerm } from '@sketch/modules-common'

const FORM_INITIAL_VALUES: FormValues = {
  projectName: '',
  projectDescription: '',
  projectWorkspaceAccessLevel: 'DEFAULT',
  workspaceMembershipIdentifiers: [],
  workspacePermissionGroupIdentifiers: [],
  guestsToInvite: [],
}

const newProjectFormSchema = (folderTerm: FolderTerm) =>
  yup.object({
    projectName: yup
      .string()
      .trim()
      .required(`A ${folderTerm.lower} name is required`),
    projectDescription: yup
      .string()
      .max(MAX_LENGTH, 'Description must be 255 characters or less'),
    projectWorkspaceAccessLevel: yup
      .mixed()
      .oneOf<CreateProjectWorkspaceAccessLevel>(['NONE', 'DEFAULT'])
      .required(),
    workspaceMembershipIdentifiers: yup.array<string[]>(),
    workspacePermissionGroupIdentifiers: yup.array<string[]>(),
    guestsToInvite: yup.array<PendingGuestMember>(),
  })

const AddNewProjectModal: React.FCC<AddNewProjectModalProps> = props => {
  const { onCompleted, workspace, hideModal } = props

  const workspaceIdentifier = workspace.identifier

  const [createProject] = useCreateProjectMutation({
    onCompleted,
    onError: 'show-toast',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: {
          workspaceId: workspaceIdentifier,
        } satisfies GetProjectsQueryVariables,
      },
    ],
  })

  const folderTerm = useFolderTerm()
  const schema = useMemo(() => newProjectFormSchema(folderTerm), [folderTerm])

  const handleOnSubmit = async (
    values: FormValues,
    formikActions: FormActions
  ) => {
    const trimmedValues = schema.cast(values) as FormValues
    formikActions.setSubmitting(true)

    const permissionGroups = (
      trimmedValues.workspacePermissionGroupIdentifiers || []
    ).map(identifier => ({ identifier, accessLevel: 'VIEW' as const }))

    try {
      await createProject({
        variables: {
          workspaceId: workspaceIdentifier,
          name: trimmedValues.projectName!,
          description: trimmedValues.projectDescription ?? '',
          workspaceAccessLevel: trimmedValues.projectWorkspaceAccessLevel,
          permissionGroups,
          workspaceMembershipIdentifiers:
            trimmedValues.workspaceMembershipIdentifiers,
          guestsToInvite: trimmedValues.guestsToInvite,
        },
      })
    } catch {
      // Ignore this error the mutation will handle it
    } finally {
      formikActions.setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={FORM_INITIAL_VALUES}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={true}
      validateOnMount={true}
    >
      {formikBag => (
        <FormikRender
          formikBag={formikBag}
          workspace={workspace}
          hideModal={hideModal}
        />
      )}
    </Formik>
  )
}

export default AddNewProjectModal
